// Global
import React from 'react';

// Local
import { CardListing } from '@generated/CardListing/Carousel.model';
import { ComponentParams, GetComponentServerProps, Item } from '@sitecore-content-sdk/nextjs';
import { Themes } from 'lib/themes';
import { usePreambleFields } from 'components/helpers/Molecules/Preamble/Preamble.sitecore';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';

// Data fetching
import {
  ContentListWithOverrides,
  DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
  getContentListWithOverrides,
} from 'lib/utils/components/content-list-data';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { CardFields, fieldTypes } from '../CardListingShared';
import { Carousel } from './Carousel';
import { ContentGridCardAlignment } from '../ContentGrid/ContentGrid';
import { ContentQueryProvider } from '../ContentGrid/ContentQueryContext';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { useDefaultCardDetailLevel } from 'lib/hooks/useSiteSettings';

export type CarouselParams = ComponentParams & {
  cardDetails: {
    fieldsToShow: Item[];
  };
  gridAspectRatio: Item;
};
type CarouselProps = Omit<CardListing.Carousel.Carousel_Component, 'params'> & {
  staticProps: ContentListWithOverrides | null;
  params?: CarouselParams;
};

const CarouselContent = (props: CarouselProps) => {
  // Get the default card detail level from site settings (handles both flat and nested shapes)
  const defaultCardDetailLevel = useDefaultCardDetailLevel();

  // Priority: 1) cardDetails from component params, 2) defaultCardDetailLevel from site settings, 3) all fields
  const fieldsToShow = props.params?.cardDetails?.fieldsToShow?.length
    ? props.params.cardDetails.fieldsToShow.map((x: Item) => x.name as CardFields)
    : defaultCardDetailLevel?.length
      ? defaultCardDetailLevel.map((x) => x.name as CardFields)
      : [...fieldTypes];

  const isEditing = useIsEditing();
  const preambleFields = usePreambleFields({
    fields: props.fields,
    ctaComponentClassOverrides: {
      cta1: 'component-content-grid-color-button-color-1',
      cta2: 'component-content-grid-color-button-color-2',
    },
  });

  const gridAspectRatio = getEnumValue<ImageAspectRatio>(props.params?.gridAspectRatio);

  // Always show placeholder in the Experience Editor, even when datasource is not yet
  // configured. Without this, the component is completely invisible to editors and they
  // cannot select it to configure the Content List datasource field.
  if (isEditing && !props.staticProps?.result) {
    const hiddenFields = fieldTypes.filter((field) => !fieldsToShow.includes(field));
    const cardDetailsSource = props.params?.cardDetails?.fieldsToShow?.length
      ? 'Card Details (Component Level)'
      : defaultCardDetailLevel?.length
        ? 'Default Card Detail Level (Site Settings)'
        : 'All Fields (Default Fallback)';
    return (
      <div data-component="authorable/shared/card-listing/carousel/carousel">
        <EditingHelpText>
          Carousel: No data available. Ensure the Content List datasource field is configured and
          that the linked content list has published items.
        </EditingHelpText>
        <EditingHelpText>Card configuration source: {cardDetailsSource}</EditingHelpText>
        <EditingHelpText>Shown fields: {fieldsToShow.join(', ')}</EditingHelpText>
        <EditingHelpText>Hidden fields: {hiddenFields.join(', ')}</EditingHelpText>
      </div>
    );
  }

  const result = (
    <ContentQueryProvider
      contentListItem={props.fields?.contentList}
      initialSearchResponse={props.staticProps?.result ?? null}
    >
      <Carousel
        theme={props.params?.theme as Themes}
        preambleOrientation={props.params?.preambleOrientation as PreambleOrientation}
        cardAlignment={props.params?.cardAlignment as ContentGridCardAlignment}
        gridAspectRatio={gridAspectRatio}
        {...preambleFields}
        contentOverrides={props.staticProps?.contentOverrides || {}}
        fallbackImageUrl={props.staticProps?.fallbackImageUrl}
        fieldsToShow={fieldsToShow}
      />
    </ContentQueryProvider>
  );
  if (isEditing) {
    const hiddenFields = fieldTypes.filter((field) => !fieldsToShow.includes(field));
    const cardDetailsSource = props.params?.cardDetails?.fieldsToShow?.length
      ? 'Card Details (Component Level)'
      : defaultCardDetailLevel?.length
        ? 'Default Card Detail Level (Site Settings)'
        : 'All Fields (Default Fallback)';
    return (
      <div>
        <EditingHelpText>Card configuration source: {cardDetailsSource}</EditingHelpText>
        <EditingHelpText>Shown fields: {fieldsToShow.join(', ')}</EditingHelpText>
        <EditingHelpText>Hidden fields: {hiddenFields.join(', ')}</EditingHelpText>
        {result}
      </div>
    );
  }
  return result;
};

export const Default = (props: CarouselProps) => {
  return <CarouselContent {...props} />;
};

export const getComponentServerProps: GetComponentServerProps = async (rendering, layoutData) => {
  try {
    const params = rendering.params as CarouselParams;
    const staticProps = await getContentListWithOverrides(rendering, layoutData, {
      maxCards: 8,
      gridAspectRatio:
        getEnumValue<ImageAspectRatio>(params.gridAspectRatio) ??
        DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
    });
    if (staticProps === null && process.env.NODE_ENV !== 'production') {
      // null means getContentListWithOverrides found no contentList field or no siteId.
      // Check that the Carousel datasource has a Content List field pointing to a valid item.
      // eslint-disable-next-line no-console
      console.warn(
        '[Carousel] getComponentServerProps: getContentListWithOverrides returned null.',
        'Rendering UID:',
        rendering.uid,
        '| contentList field value:',
        (rendering.fields as { contentList?: unknown } | undefined)?.contentList ?? '(not set)'
      );
    }
    return { staticProps };
  } catch (error) {
    // Log explicitly so the cause is visible in the terminal alongside the CSDK's own error output.
    // The CSDK will re-catch this throw, log it as "[PageDataPlugin] getComponentData error",
    // and the component will receive staticProps === undefined at render time.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[Carousel] getComponentServerProps threw an error:', error);
    }
    throw error;
  }
};
